/**
 * useReferralDocuments - Custom Hook for Referral Document Management
 *
 * Provides a clean interface for managing referral documents including
 * fetching, downloading, uploading, and state management. Uses ApiService instead
 * of direct domain calls for better architecture and consistency.
 *
 * Features:
 * - Document fetching with pagination
 * - Document downloading with error handling
 * - Document uploading with progress tracking
 * - Loading and error state management
 * - Retry functionality
 * - Performance optimization with useCallback
 *
 * @module hooks/useReferralDocuments
 */

import { useState, useCallback } from 'react';
import { apiService } from '@/services/ApiService';
import type {
  ReferralDocument,
  ListDocumentsResponse,
  ListDocumentsRequest,
  UploadDocumentRequest
} from '@/services/domains/ReferralDomain';
import { FileUtils } from '@/utils/fileUtils';
import { getLogger } from '@/services/shared/LoggerFactory';

// File validation constants - moved outside component to prevent recreation
const SUPPORTED_TYPES = {
  'PDF': 'application/pdf',
  'DOC': 'application/msword',
  'DOCX': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
} as const;

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

/**
 * Pagination interface for document list
 */
export interface DocumentPagination {
  page: number;
  totalPages: number;
  total: number;
  perPage: number;
}

/**
 * Upload progress interface
 */
export interface UploadProgress {
  isUploading: boolean;
  progress: number;
  fileName?: string;
}

/**
 * File validation result interface
 */
export interface FileValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Upload options interface
 */
export interface UploadOptions {
  description?: string;
  documentType?: string;
}

/**
 * Return interface for useReferralDocuments hook
 */
export interface UseReferralDocumentsReturn {
  /** Array of referral documents */
  documents: ReferralDocument[];
  /** Loading state for document operations */
  loading: boolean;
  /** Error message if any operation fails */
  error: string | null;
  /** Pagination information */
  pagination: DocumentPagination;
  /** Upload progress information */
  uploadProgress: UploadProgress;
  /** Function to fetch documents with referralId and optional page parameter */
  fetchDocuments: (referralId: string, page?: number) => Promise<void>;
  /** Function to download a document by ID and filename */
  downloadDocument: (documentId: string, filename: string) => Promise<void>;
  /** Function to upload a document file */
  uploadDocument: (referralId: string, file: File, options?: UploadOptions) => Promise<ReferralDocument | null>;
  /** Function to delete a document by ID */
  deleteDocument: (documentId: string) => Promise<boolean>;
  /** Function to validate a file before upload */
  validateFile: (file: File) => FileValidationResult;
  /** Function to retry the last failed fetch operation */
  retryFetch: (referralId: string) => Promise<void>;
}

/**
 * Custom hook for managing referral documents
 *
 * Provides a clean interface for document operations while handling
 * all state management, error handling, and loading states internally.
 *
 * @returns UseReferralDocumentsReturn interface with documents and operations
 */
export const useReferralDocuments = (): UseReferralDocumentsReturn => {
  const logger = getLogger();
  // State management
  const [documents, setDocuments] = useState<ReferralDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<DocumentPagination>({
    page: 1,
    totalPages: 1,
    total: 0,
    perPage: 10,
  });
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({
    isUploading: false,
    progress: 0,
  });

  // Store last fetch parameters for retry functionality
  const [lastFetchParams, setLastFetchParams] = useState<{ referralId: string; page: number } | null>(null);

  /**
   * Validate a file before upload
   *
   * @param file - File to validate
   * @returns Validation result with error message if invalid
   */
  const validateFile = useCallback((file: File): FileValidationResult => {
    const supportedMimeTypes = Object.values(SUPPORTED_TYPES);
    if (!supportedMimeTypes.includes(file.type as typeof supportedMimeTypes[number])) {
      const supportedFormats = Object.keys(SUPPORTED_TYPES).join(', ');
      return {
        isValid: false,
        error: `Please upload a supported file type (${supportedFormats}).`
      };
    }

    if (file.size > MAX_FILE_SIZE) {
      return {
        isValid: false,
        error: 'File size must be less than 10MB.'
      };
    }

    return { isValid: true };
  }, []); // No dependencies needed since constants are now outside component


  /**
   * Fetch documents from the API
   *
   * @param referralId - The ID of the referral to fetch documents for
   * @param page - Page number to fetch (defaults to 1)
   */
  const fetchDocuments = useCallback(async (referralId: string, page: number = 1): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      setLastFetchParams({ referralId, page });

      const request: ListDocumentsRequest = {
        page,
        perPage: pagination.perPage,
      };

      const response: ListDocumentsResponse = await apiService.referral.getDocumentsByReferral(referralId, request);
      setDocuments(response.documents);
      setPagination({
        page: response.page,
        totalPages: response.total_pages,
        total: response.total,
        perPage: response.per_page,
      });
    } catch (error) {
      logger.error('Error fetching documents:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load documents';
      setError(errorMessage);
      setDocuments([]);
      setPagination({
        page: 1,
        totalPages: 1,
        total: 0,
        perPage: pagination.perPage,
      });
    } finally {
      setLoading(false);
    }
  }, [pagination.perPage]);

  /**
   * Upload a document file
   *
   * @param referralId - The ID of the referral to upload document for
   * @param file - File to upload
   * @param options - Upload options including description and document type
   * @returns Promise resolving to uploaded document or null if failed
   */
  const uploadDocument = useCallback(async (referralId: string, file: File, options: UploadOptions = {}): Promise<ReferralDocument | null> => {
    // Validate file first
    const validation = validateFile(file);
    if (!validation.isValid) {
      setError(validation.error || 'Invalid file');
      return null;
    }

    try {
      setUploadProgress({
        isUploading: true,
        progress: 0,
        fileName: file.name,
      });
      setError(null);

      // Convert file to base64
      setUploadProgress(prev => ({ ...prev, progress: 25 }));
      let fileData: string;
      try {
        fileData = await FileUtils.convertToBase64String(file);
      } catch (error) {
        logger.error('Error reading file:', error);
        setError('Failed to read file');
        return null;
      }

      // Prepare upload request
      setUploadProgress(prev => ({ ...prev, progress: 50 }));
      const uploadRequest: UploadDocumentRequest = {
        referralId,
        fileData,
        filename: file.name,
        mimeType: file.type,
        description: options.description,
      };

      // Upload document
      setUploadProgress(prev => ({ ...prev, progress: 75 }));
      const response: ReferralDocument = await apiService.referral.uploadDocument(uploadRequest);

      setUploadProgress(prev => ({ ...prev, progress: 100 }));
      await fetchDocuments(referralId, pagination.page);
      return response;
    } catch (error) {
      logger.error('Error uploading document:', error);
      if (error instanceof Error && error.message === 'Failed to read file') {
        setError('Failed to read file');
      } else {
        const errorMessage = error instanceof Error ? error.message : 'Failed to upload document';
        setError(errorMessage);
      }
      return null;
    } finally {
      setUploadProgress({
        isUploading: false,
        progress: 0,
      });
    }
  }, [validateFile, fetchDocuments, pagination.page]);

  /**
   * Download a document by ID
   *
   * @param documentId - ID of the document to download
   * @param filename - Filename to use for the downloaded file
   */
  const downloadDocument = useCallback(async (documentId: string, filename: string): Promise<void> => {
    try {
      const blob = await apiService.referral.downloadDocument(documentId);

      // Create download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      logger.error('Error downloading document:', error);
      // Note: In a production app, you might want to show a toast notification here
      // For now, we'll just log the error as the original implementation did
    }
  }, []);

  /**
   * Delete a document by ID
   *
   * @param documentId - ID of the document to delete
   * @returns Promise resolving to boolean indicating success
   */
  const deleteDocument = useCallback(async (documentId: string): Promise<boolean> => {
    try {
      setError(null);
      await apiService.referral.deleteDocument(documentId);

      // Note: After deletion, you may need to refetch documents
      // This would require the referralId to be passed or stored
      return true;
    } catch (error) {
      logger.error('Error deleting document:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete document';
      setError(errorMessage);
      return false;
    }
  }, []);

  /**
   * Retry the last failed fetch operation
   *
   * @param referralId - The ID of the referral to retry fetch for
   */
  const retryFetch = useCallback(async (referralId: string): Promise<void> => {
    if (lastFetchParams && lastFetchParams.referralId === referralId) {
      await fetchDocuments(referralId, lastFetchParams.page);
    } else {
      await fetchDocuments(referralId, 1);
    }
  }, [fetchDocuments, lastFetchParams]);

  return {
    documents,
    loading,
    error,
    pagination,
    uploadProgress,
    fetchDocuments,
    downloadDocument,
    uploadDocument,
    deleteDocument,
    validateFile,
    retryFetch,
  };
};

export default useReferralDocuments;